/**
 * House slug utilities for URL handling
 *
 * Provides functions to:
 * - Generate URL-safe slugs from house names
 * - Find houses by slug
 * - Validate house name uniqueness
 */

import type { House } from '@liveschool/types';

/**
 * Convert a house name to a URL-safe slug
 *
 * @example
 * slugifyHouseName('Golden Eagles') // => 'golden-eagles'
 * slugifyHouseName('Red') // => 'red'
 * slugifyHouseName("O'Brien's Team") // => 'obriens-team'
 */
export function slugifyHouseName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/['']/g, '') // Remove apostrophes
    .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Find a house by its URL slug
 *
 * @param houses Array of houses to search
 * @param slug URL slug to match
 * @returns The matching house, or undefined if not found
 *
 * @example
 * const house = findHouseBySlug(houses, 'golden-eagles');
 */
export function findHouseBySlug(houses: House[], slug: string): House | undefined {
  return houses.find((house) => slugifyHouseName(house.name) === slug);
}

/**
 * Check if a house name is unique among all houses
 *
 * @param houses Array of all houses
 * @param name Name to check for uniqueness
 * @param excludeId Optional house ID to exclude (for editing existing house)
 * @returns True if the name is unique, false otherwise
 *
 * @example
 * // Check if "Red" is unique (for new house)
 * isHouseNameUnique(houses, 'Red')
 *
 * // Check if "Red" is unique (when editing house-1)
 * isHouseNameUnique(houses, 'Red', 'house-1')
 */
export function isHouseNameUnique(
  houses: House[],
  name: string,
  excludeId?: string
): boolean {
  const normalizedName = name.trim().toLowerCase();
  return !houses.some(
    (house) =>
      house.name.trim().toLowerCase() === normalizedName &&
      house.id !== excludeId
  );
}
